import { parseService } from '@/services/parse/parse.service'

export const getNodeText = (node: any): string => {
  if (node === null || node === undefined) {
    return ''
  }
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node)
  }
  if (Array.isArray(node)) {
    return node.map(getNodeText).join('')
  }
  if (node._cdata !== undefined) {
    return Array.isArray(node._cdata)
      ? node._cdata.map((t: any) => String(t || '')).join('')
      : String(node._cdata || '')
  }
  if (node._text !== undefined) {
    return Array.isArray(node._text)
      ? node._text.map((t: any) => String(t || '')).join('')
      : String(node._text || '')
  }
  return ''
}

/** Корни разметки: внешний объект form и вложенный form.form */
function getFormRoots(pageData: any): any[] {
  const roots: any[] = []
  const f = pageData?.form
  if (!f || typeof f !== 'object') {
    return roots
  }
  roots.push(f)
  const inner = f.form
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    roots.push(inner)
  }
  return roots
}

function resolveRawFormTitle(pageData: any): string {
  for (const root of getFormRoots(pageData)) {
    const raw =
      getNodeText(root?.tshort) ||
      getNodeText(root?.description) ||
      getNodeText(root?.name) ||
      ''
    if (raw) {
      return raw
    }
  }
  return ''
}

function resolvePanelFallbackTitle(pageData: any): string {
  for (const root of getFormRoots(pageData)) {
    const panels = root?.panels
    if (panels && typeof panels === 'object') {
      const firstPanelKey = Object.keys(panels)[0]
      const firstPanel = panels[firstPanelKey]
      const text = firstPanel?.description?._text
      if (text) {
        return String(text)
      }
    }
  }
  return ''
}

export function unwrapPageFieldValue(field: any): any {
  if (field === null || field === undefined) {
    return undefined
  }
  if (typeof field !== 'object') {
    return field
  }
  if ('value' in field) {
    const value = (field as any).value
    if (value && typeof value === 'object' && 'value' in value) {
      return (value as any).value
    }
    return value
  }
  return field
}

const TITLE_TEMPLATE_VAR_PATTERNS: RegExp[] = [
  /\$\{\s*_\.\s*(?:getFieldAsString|getFieldAsLong|getFieldAsBoolean|getField)\s*\(\s*['"]([^'"]+)['"]/gi,
  /#if\s*\(\s*\$\{\s*(\w+)\s*\}\s*==/gi,
  /#elseif\s*\(\s*\$\{\s*(\w+)\s*\}\s*==/gi,
  /#if\s*\(\s*\$(\w+)\s*==/gi,
  /#elseif\s*\(\s*\$(\w+)\s*==/gi,
  /#if\s*\(\s*\$\{\s*(\w+)\s*\}\s*\)/gi,
  /#if\s*\(\s*\$(\w+)\s*\)/gi,
  /\$\{\s*(?!_\.)(\w+)\s*\}/g,
  /\$(\w+)(?=\s*==)/g,
]

/** Имена полей, упомянутых в tshort/Velocity-шаблоне заголовка. */
export function extractTitleTemplateVarNames(template: string): string[] {
  if (!template || typeof template !== 'string') {
    return []
  }
  const names = new Set<string>()
  for (const pattern of TITLE_TEMPLATE_VAR_PATTERNS) {
    pattern.lastIndex = 0
    let match: RegExpExecArray | null
    while ((match = pattern.exec(template)) !== null) {
      const name = match[1]?.trim()
      if (name && name !== '_') {
        names.add(name)
      }
    }
  }
  return [...names]
}

function hasResolvedFieldValue(value: unknown): boolean {
  return value !== undefined && value !== null && value !== ''
}

function valueFromPageFieldKey(pageFields: Record<string, any>, key: string): unknown {
  if (!(key in pageFields)) {
    return undefined
  }
  return unwrapPageFieldValue(pageFields[key])
}

export function resolveTitleTemplateFieldValue(
  pageFields: Record<string, any>,
  varName: string,
): unknown {
  const name = String(varName ?? '').trim()
  if (!name) {
    return undefined
  }

  const exact = valueFromPageFieldKey(pageFields, name)
  if (hasResolvedFieldValue(exact)) {
    return exact
  }

  const suffixMatches: string[] = []
  for (const key of Object.keys(pageFields)) {
    if (key.endsWith(`.${name}`)) {
      suffixMatches.push(key)
    }
  }
  suffixMatches.sort((a, b) => b.length - a.length)
  for (const key of suffixMatches) {
    const v = valueFromPageFieldKey(pageFields, key)
    if (hasResolvedFieldValue(v)) {
      return v
    }
  }

  const idStemMatch = name.match(/^(.+?)Id$/i)
  if (idStemMatch) {
    const stem = idStemMatch[1]
    const stemLower = stem.toLowerCase()
    for (const key of Object.keys(pageFields)) {
      if (!key.endsWith('._id')) {
        continue
      }
      const keyLower = key.toLowerCase()
      if (keyLower.includes(stemLower)) {
        const v = valueFromPageFieldKey(pageFields, key)
        if (hasResolvedFieldValue(v)) {
          return v
        }
      }
    }
  }

  return undefined
}

function resolveCompareValueFromTemplate(
  template: string,
  pageFields: Record<string, any>,
  formIdNum: number | undefined,
): unknown {
  for (const varName of extractTitleTemplateVarNames(template)) {
    const value = resolveTitleTemplateFieldValue(pageFields, varName)
    if (hasResolvedFieldValue(value)) {
      return value
    }
  }
  if (Number.isFinite(formIdNum)) {
    return formIdNum
  }
  return undefined
}

export const getFormTitle = (pageData: any): string => {
  if (!pageData) return ''
  const isFormLoaded = pageData?.form === undefined

  let rawTitle = resolveRawFormTitle(pageData)
  if (!rawTitle) {
    rawTitle = String(pageData?.title || '') || (isFormLoaded ? 'Загрузка...' : '')
  }

  if (!rawTitle) {
    const panelTitle = resolvePanelFallbackTitle(pageData)
    if (panelTitle) {
      return panelTitle
    }
  }

  if (!rawTitle) {
    return ''
  }

  const pageFields = {
    ...(pageData?.uniData?.fields?.fields || {}),
    ...(pageData?.jsResult?.changed_data?.fields?.fields || {}),
  }

  const formIdFromMeta =
    pageData?.form?.form?._attributes?.id ??
    pageData?.form?.form?._attributes?.fid ??
    pageData?.form?._attributes?.id ??
    pageData?.form?._attributes?.fid ??
    pageData?.jsResult?.form_id
  const formIdNum =
    formIdFromMeta != null
      ? typeof formIdFromMeta === 'number'
        ? formIdFromMeta
        : parseInt(String(formIdFromMeta), 10)
      : undefined

  const compareValue = resolveCompareValueFromTemplate(rawTitle, pageFields, formIdNum)

  const contextObject = {
    valueOf: () => compareValue ?? 0,
    hasField: (fieldName: string) => {
      const v = resolveTitleTemplateFieldValue(pageFields, fieldName)
      return hasResolvedFieldValue(v)
    },
  }

  const resolveTitleField = (varName: string): any => {
    if (varName === '_') {
      return contextObject
    }
    const value = resolveTitleTemplateFieldValue(pageFields, varName)
    return hasResolvedFieldValue(value) ? value : ''
  }

  const evaluated = parseService.literator(rawTitle, resolveTitleField)

  const lastCharBeforeDollar = typeof evaluated === 'string' && evaluated.length >= 2 ? evaluated[evaluated.length - 2] : ''
  const trimTrailingDollar =
    typeof evaluated === 'string' && evaluated.endsWith('$') && /[a-zA-Zа-яА-ЯёЁ0-9]/.test(lastCharBeforeDollar)
  return trimTrailingDollar ? (evaluated as string).slice(0, -1) : evaluated
}
